/**
 * Per-user UI preferences, persisted to the account so customizations follow the
 * login across devices (founder 2026-06-30). Backed by GET/PUT /ui-prefs, with a
 * localStorage cache for instant first paint (no flicker waiting on the network).
 *
 *   const [layout, setLayout] = useUiPref<string[]>("dash-layout", DEFAULT_LAYOUT)
 *
 * Read order: server value (source of truth) once it arrives → localStorage cache
 * → the supplied fallback. Writes go to localStorage immediately + the account in
 * the background (best-effort; stays local if offline).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "./api-context.js";

const lsKey = (key: string): string => `dd-uipref-${key}`;

function readLocal<T>(key: string, fallback: T): T {
  try {
    const s = localStorage.getItem(lsKey(key));
    return s ? (JSON.parse(s) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeLocal<T>(key: string, value: T): void {
  try { localStorage.setItem(lsKey(key), JSON.stringify(value)); } catch { /* ignore */ }
}

/** All of the signed-in user's prefs, fetched once and cached app-wide. */
export function useAllUiPrefs(): Record<string, unknown> | undefined {
  const api = useApiClient();
  return useQuery({
    queryKey: ["ui-prefs"],
    queryFn: () => api.get<Record<string, unknown>>("/ui-prefs"),
    staleTime: 5 * 60_000,
  }).data;
}

export function useUiPref<T>(key: string, fallback: T): [T, (v: T) => void] {
  const api = useApiClient();
  const qc = useQueryClient();
  const server = useAllUiPrefs();
  const [value, setValue] = useState<T>(() => readLocal(key, fallback));
  const adopted = useRef(false);

  // Adopt the account's value once it arrives (source of truth across devices),
  // but only the first time, so we never clobber an edit made while it loaded.
  useEffect(() => {
    if (adopted.current || !server) return;
    adopted.current = true;
    if (Object.prototype.hasOwnProperty.call(server, key)) {
      const sv = server[key] as T;
      setValue(sv);
      writeLocal(key, sv);
    }
  }, [server, key]);

  const set = useCallback((v: T) => {
    setValue(v);
    writeLocal(key, v);
    qc.setQueryData<Record<string, unknown>>(["ui-prefs"], (cur) => ({ ...(cur ?? {}), [key]: v }));
    api.put(`/ui-prefs/${encodeURIComponent(key)}`, v).catch(() => { /* stays local if the save fails */ });
  }, [api, qc, key]);

  return [value, set];
}
